import React, { useEffect, useState } from 'react';
import { auth } from '../../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db } from '../../firebase';
import { doc, setDoc, collection, deleteDoc, onSnapshot } from 'firebase/firestore';
import './AddBarForm.css';
import { 
  FaUserPlus, FaGlassWhiskey, FaUser, FaEnvelope, FaLock, FaEdit, FaTrash, 
  FaCheck, FaTimes, FaExclamationTriangle, FaCheckCircle, FaDownload,
  FaSpinner, FaIdCard, FaUserTag
} from 'react-icons/fa';

const AddBarForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [bars, setBars] = useState([]);
  const [selectedBar, setSelectedBar] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editingBar, setEditingBar] = useState(null);

  useEffect(() => {
    const barsCollection = collection(db, 'identifications');
    
    const unsubscribe = onSnapshot(barsCollection, (snapshot) => {
      const barList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBars(barList);
    }, (error) => {
      console.error("Erreur lors de la récupération des barmans : ", error);
    });

    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (selectedBar) {
        // Mise à jour uniquement du nom et du prénom
        const barDoc = doc(db, 'identifications', selectedBar.id);
        await setDoc(barDoc, {
          ...selectedBar, // Conserver les données existantes
          firstName,
          lastName,
        });

        setSuccess('Nom et prénom mis à jour avec succès !');
      } else {
        // Ajouter un nouveau barman
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        await setDoc(doc(db, 'identifications', user.uid), {
          email: user.email,
          firstName,
          lastName,
          role: 'bar',
          createdAt: new Date(),
        });
        setSuccess('Barman ajouté avec succès !');
      }

      // Réinitialiser les champs
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      setSelectedBar(null);
    } catch (error) {
      setError('Erreur lors de l\'ajout ou de la mise à jour du barman : ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (bar) => {
    setEditingBar(bar);
    setShowEditPopup(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const barDoc = doc(db, 'identifications', editingBar.id);
      await setDoc(barDoc, {
        ...editingBar,
        firstName: editingBar.firstName,
        lastName: editingBar.lastName,
      });

      setSuccess('Barman mis à jour avec succès !');
      setShowEditPopup(false);
      setEditingBar(null);
    } catch (error) {
      setError('Erreur lors de la mise à jour du barman : ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (serverId) => {
    try {
      // Appeler le backend pour supprimer l'utilisateur dans Firebase Authentication
      const response = await fetch('https://skaneat-app.vercel.app/delete-user', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uid: serverId }),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la suppression de l’utilisateur.');
      }
  
      // Supprimer l'utilisateur dans Firestore Database
      await deleteDoc(doc(db, 'identifications', serverId));
      await deleteDoc(doc(db, 'horaires', serverId));
  
      setSuccess('Serveur supprimé avec succès !');
    } catch (error) {
      setError('Erreur lors de la suppression du barman/barmaid : ' + error.message);
    }
  };  

  const exportToCSV = () => {
    const today = new Date().toLocaleDateString('fr-FR');
    const headers = ['Nom', 'Prenom', 'Email', 'Role', 'Date de creation'];
    
    // Formater les données
    const csvData = bars
      .filter((bar) => bar.role === 'bar')
      .map((bar) => {
        const createdAt = bar.createdAt?.toDate 
          ? bar.createdAt.toDate().toLocaleDateString('fr-FR')
          : 'Non specifie';
        
        return [
          bar.firstName,
          bar.lastName,
          bar.email,
          bar.role,
          createdAt
        ].join(';');
      });
    
    // Assembler le contenu CSV
    const csvContent = [
      headers.join(';'),
      ...csvData
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `barmans_${today.replace(/\//g, '-')}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bar-management-container">
      {/* Header */}
      <div className="bar-management-header">
        <h1 className="bar-management-title">
          <FaGlassWhiskey className="bar-title-icon" />
          Gestion des Barmans
        </h1>
        <p className="bar-management-subtitle">
          Ajoutez et gérez les comptes barmans de votre restaurant
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="bar-message bar-error-message">
          <FaExclamationTriangle className="bar-message-icon" />
          {error}
        </div>
      )}
      {success && (
        <div className="bar-message bar-success-message">
          <FaCheckCircle className="bar-message-icon" />
          {success}
        </div>
      )}

      {/* Form Section */}
      <div className="bar-form-section">
        <h2 className="bar-form-title">
          <FaUserPlus className="bar-form-icon" />
          Ajouter un barman
        </h2>
        
        <form className="bar-form" onSubmit={handleSubmit}>
          <div className="bar-form-row">
            <div className="bar-form-group">
              <label className="bar-form-label">
                <FaUser className="bar-label-icon" />
                Nom
              </label>
              <input
                type="text"
                className="bar-form-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Ex: Dupont"
                required
              />
            </div>
            <div className="bar-form-group">
              <label className="bar-form-label">
                <FaIdCard className="bar-label-icon" />
                Prénom
              </label>
              <input
                type="text"
                className="bar-form-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Ex: Jean"
                required
              />
            </div>
          </div>

          <div className="bar-form-row">
            <div className="bar-form-group">
              <label className="bar-form-label">
                <FaEnvelope className="bar-label-icon" />
                Email du barman
              </label>
              <input
                type="email"
                className="bar-form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Ex: jean.dupont@bar.com"
                required
              />
            </div>
            <div className="bar-form-group">
              <label className="bar-form-label">
                <FaLock className="bar-label-icon" />
                Mot de passe du barman
              </label>
              <input
                type="password"
                className="bar-form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mot de passe sécurisé"
                required
              />
            </div>
          </div>

          <div className="bar-form-actions">
            <button 
              type="submit" 
              className="bar-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <FaSpinner className="spinning" />
                  Ajout en cours...
                </>
              ) : (
                <>
                  <FaCheck />
                  Ajouter un barman
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Bar List Section */}
      <div className="bar-list-section">
        <div className="bar-list-header">
          <h3 className="bar-list-title">
            <FaUserTag className="bar-list-icon" />
            Barmans Enregistrés
          </h3>
          <button onClick={exportToCSV} className="bar-export-btn">
            <FaDownload />
            Exporter en CSV
          </button>
        </div>
        
        {bars.filter((bar) => bar.role === 'bar').length === 0 ? (
          <div className="bar-empty-state">
            <FaGlassWhiskey className="bar-empty-icon" />
            <p className="bar-empty-text">Aucun barman enregistré pour le moment</p>
          </div>
        ) : (
          <ul className="bar-list">
            {bars
              .filter((bar) => bar.role === 'bar')
              .map((bar) => (
                <li key={bar.id} className="bar-list-item">
                  <div className="bar-info">
                    <div className="bar-name">
                      {bar.firstName} {bar.lastName}
                    </div>
                    <div className="bar-email">
                      {bar.email}
                    </div>
                    <div className="bar-role-badge">
                      Barman
                    </div>
                  </div>
                  <div className="bar-actions">
                    <button 
                      className="bar-action-btn bar-edit-btn"
                      onClick={() => handleEdit(bar)}
                    >
                      <FaEdit />
                      Modifier
                    </button>
                    <button 
                      className="bar-action-btn bar-delete-btn"
                      onClick={() => handleDelete(bar.id)}
                    >
                      <FaTrash />
                      Supprimer
                    </button>
                  </div>
                </li>
              ))}
          </ul>
        )}
      </div>

      {/* Edit Bar Popup */}
      {showEditPopup && editingBar && (
        <div className="bar-popup-overlay" onClick={() => setShowEditPopup(false)}>
          <div className="bar-popup" onClick={e => e.stopPropagation()}>
            <div className="bar-popup-header">
              <h3 className="bar-popup-title">
                <FaEdit className="bar-popup-icon" />
                Modifier le barman
              </h3>
            </div>
            
            <div className="bar-popup-body">
              <form className="bar-form" onSubmit={handleEditSubmit}>
                <div className="bar-form-row">
                  <div className="bar-form-group">
                    <label className="bar-form-label">
                      <FaUser className="bar-label-icon" />
                      Nom
                    </label>
                    <input
                      type="text"
                      className="bar-form-input"
                      value={editingBar.firstName || ''}
                      onChange={(e) => setEditingBar({
                        ...editingBar,
                        firstName: e.target.value
                      })}
                      placeholder="Ex: Dupont"
                      required
                    />
                  </div>
                  <div className="bar-form-group">
                    <label className="bar-form-label">
                      <FaIdCard className="bar-label-icon" />
                      Prénom
                    </label>
                    <input
                      type="text"
                      className="bar-form-input"
                      value={editingBar.lastName || ''}
                      onChange={(e) => setEditingBar({
                        ...editingBar,
                        lastName: e.target.value
                      })}
                      placeholder="Ex: Jean"
                      required
                    />
                  </div>
                </div>

                <div className="bar-form-group">
                  <label className="bar-form-label">
                    <FaEnvelope className="bar-label-icon" />
                    Email
                  </label>
                  <input
                    type="email"
                    className="bar-form-input"
                    value={editingBar.email || ''}
                    disabled
                    style={{ background: 'var(--bar-bg-color)', color: 'var(--bar-text-secondary)' }}
                  />
                  <small style={{ color: 'var(--bar-text-secondary)', fontSize: '0.75rem' }}>
                    L'email ne peut pas être modifié
                  </small>
                </div>
              </form>
            </div>

            <div className="bar-popup-actions">
              <button 
                className="bar-popup-btn primary" 
                onClick={handleEditSubmit}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FaSpinner className="spinning" />
                    Mise à jour...
                  </>
                ) : (
                  <>
                    <FaCheck />
                    Mettre à jour
                  </>
                )}
              </button>
              <button 
                className="bar-popup-btn secondary" 
                onClick={() => {
                  setShowEditPopup(false);
                  setEditingBar(null);
                }}
              >
                <FaTimes />
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddBarForm;
